import Link from "next/link";
import { signIn } from "src/utils/nextAuth";
import { Heading, Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { AuthProvider } from "pages/api/auth/getUserTokenFromApi";
import { Container } from "src/atoms/Container";
import { LoginForm } from "src/molecules/LoginForm";
import { SocialButton } from "src/atoms/SocialButton";
import { t, Trans } from "@lingui/macro";
import { useRouter, withRouter } from "next/router";
import { PageHead } from "src/molecules/PageHead";
import { useLingui } from "@lingui/react";
import NoSSR from "src/atoms/NoSSR";
import { getQueryString } from "src/modules/page/utils";
import {
  getStorageJSON,
  removeStorageItem,
  setStorageJSON,
} from "src/utils/helper/localStorage";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_POPUP_SOURCE,
} from "src/constants/gatedItem";
import { useEffect } from "react";

const LoginView = () => {
  const { i18n } = useLingui();
  const { query } = useRouter();

  const queryString = getQueryString(query);

  const registerUrl = queryString ? `/register/?${queryString}` : "/register/";

  const handleRegisterClick = () => {
    const gatedRedirectStorage: { gatedActionType?: string } =
      getStorageJSON(GATED_POPUP_REDIRECT_DATA) || {};

    if (gatedRedirectStorage.gatedActionType) {
      gatedRedirectStorage.gatedActionType = "Register";

      setStorageJSON(GATED_POPUP_REDIRECT_DATA, gatedRedirectStorage);
    }
  };

  useEffect(() => {
    if (query?.source !== GATED_POPUP_SOURCE) {
      removeStorageItem(GATED_POPUP_REDIRECT_DATA);
    }
  }, [query]);

  return (
    <Container mt={["48px", "48px", "48px", 6]} px={5} data-testid="login-page">
      <PageHead title={t(i18n)`Sign in | Otrium`} />
      <NoSSR>
        <Box
          width={["100%", "100%", "414px", "750px"]}
          sx={{ textAlign: "center", margin: "0 auto" }}
        >
          <Heading
            data-testid="login-page-heading"
            my={2}
            as="h2"
            fontSize={["40px", "40px", "40px", "48px"]}
            fontFamily="PlusJakartaSans"
            letterSpacing="normal"
            lineHeight={["1.2", "1.2", "1.2", "1.33"]}
            sx={{
              textTransform: "none",
            }}
          >
            <Trans>Log in</Trans>
          </Heading>
          <Text
            mt={3}
            mb={[5, 5, 5, "48px"]}
            fontSize="18px"
            letterSpacing="0.8px"
            lineHeight="1.56"
            data-testid="login-page-welcome-message"
          >
            <Trans>Welcome back, choose your preferred way to continue.</Trans>
          </Text>
          <Flex flexDirection={["column", "column", "column", "row"]}>
            <Box
              width={["100%", "100%", "100%", "50%"]}
              pl={0}
              pr={[0, 0, 0, "48px"]}
              sx={{
                borderRight: ["none", "none", "none", "1px solid #cccccc"],
              }}
              mt={[3, 3, 3, 0]}
            >
              <Heading
                as="h3"
                mb={3}
                textAlign={["center", "center", "center", "left"]}
                fontSize={3}
                fontFamily="PlusJakartaSans"
                fontWeight="bold"
                letterSpacing="normal"
                lineHeight="1.33"
                sx={{
                  textTransform: "none",
                }}
              >
                <Trans>Easy connect</Trans>
              </Heading>
              <Box width={["100%", "100%", "100%", "320px"]}>
                <SocialButton
                  data-testid="login-page-fb-button"
                  fullWidth
                  type="facebook"
                  onClick={() =>
                    void signIn(AuthProvider.FACEBOOK, {
                      assumedAuthType: "sign_in",
                    })
                  }
                >
                  Facebook
                </SocialButton>
                <SocialButton
                  data-testid="login-page-google-button"
                  fullWidth
                  type="google"
                  onClick={() =>
                    void signIn(AuthProvider.GOOGLE, {
                      assumedAuthType: "sign_in",
                    })
                  }
                >
                  Google
                </SocialButton>
              </Box>
            </Box>
            <Box
              width={["100%", "100%", "100%", "50%"]}
              pl={[0, 0, 0, "48px"]}
              pr={[0, 0, 0, 3]}
              mt={[5, 5, 5, 0]}
            >
              <Heading
                as="h3"
                textAlign={["center", "center", "center", "left"]}
                fontSize={3}
                fontFamily="PlusJakartaSans"
                fontWeight="bold"
                letterSpacing="normal"
                lineHeight="1.33"
                sx={{
                  textTransform: "none",
                }}
              >
                <Trans>Email</Trans>
              </Heading>
              <Box>
                <LoginForm data-testid="login-page-login-form" />
              </Box>
            </Box>
          </Flex>
          <Box
            width={["100%", "100%", "100%", "50%"]}
            ml={[0, 0, 0, "50%"]}
            mt={2}
            pl={[0, 0, 0, 3]}
          >
            <Link
              data-testid="login-page-forgot-password-link"
              href="/reset-password/"
              passHref
              legacyBehavior
            >
              <Text
                as="a"
                ml={2}
                color="tone.black"
                fontSize={1}
                fontWeight="600"
                letterSpacing="0.4px"
                lineHeight="1.5"
                sx={{
                  textDecoration: "underline",
                  ":hover": { cursor: "pointer" },
                }}
              >
                <Trans>Forgot password</Trans>
              </Text>
            </Link>
          </Box>
          <Box my={"48px"}>
            <Text
              color="tone.black"
              fontSize={1}
              letterSpacing="0.4px"
              lineHeight="1.5"
            >
              <Trans>New to Otrium?</Trans>
              <Link
                href={registerUrl}
                passHref
                data-testid="login-page-register-link"
                onClick={handleRegisterClick}
              >
                <Text
                  as="a"
                  pl={1}
                  fontWeight="600"
                  color="tone.black"
                  sx={{
                    textDecoration: "underline",
                    ":hover": { cursor: "pointer" },
                  }}
                >
                  <Trans>Register</Trans>
                </Text>
              </Link>
            </Text>
          </Box>
        </Box>
      </NoSSR>
    </Container>
  );
};
export default withRouter(LoginView);
